import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ruler, Gem, Truck, Instagram, MessageCircle, Mail, MapPin } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import HeroSlider from "@/components/HeroSlider";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

const VALUES = [
  { icon: Ruler, ar: ["تفصيل حسب المقاس", "كل عباية تُخاط خصيصاً على مقاساتكِ وتفاصيلك"], en: ["Made to Your Measurements", "Each abaya is tailored to your exact measurements and details"] },
  { icon: Gem, ar: ["عناية فائقة بالتفاصيل", "خامات فاخرة وقصّات مدروسة بعناية"], en: ["Crafted with Care", "Premium fabrics and thoughtfully considered cuts"] },
  { icon: Truck, ar: ["تجهيز وتوصيل موثوق", "من 5 إلى 10 أيام عمل داخل الكويت"], en: ["Reliable Delivery", "5 to 10 working days within Kuwait"] },
];

const STORY_AR = [
  "لم تولد رَنف لاين لتكون مجرد علامة تقدم العبايات والأزياء، بل وُلدت من شغفٍ بالأناقة، وإيمانٍ بأن كل امرأة تستحق أن تجد ما يشبهها.",
  "بدأت رحلتنا بفكرة بسيطة… أن نقدم تصاميم تجمع بين الرقي والراحة، بين الأصالة واللمسة العصرية، وبين التفاصيل الهادئة والحضور الذي لا يُنسى.",
  "في رَنف لاين نهتم بكل تفصيل، من اختيار الأقمشة إلى التصميم واللمسات الأخيرة، لنقدم لكِ قطعًا تشعرين بأنها صُممت خصيصًا لتناسبكِ وترافقكِ في مختلف تفاصيل حياتكِ.",
  "نحن نؤمن أن الأناقة لا تتبع قالبًا واحدًا، وأن لكل امرأة ذوقها وشخصيتها وطريقتها الخاصة في التعبير عن نفسها.",
  "لذلك نسعى في رَنف لاين إلى تقديم عبايات ودراريع وتصاميم مختارة بعناية، تجمع بين الجمال والبساطة والرقي، لتكون جزءًا من إطلالتكِ وقصتكِ.",
  "رَنف لاين ليس مجرد ما ترتدينه… بل إحساس يرافقكِ، وثقة تظهر في تفاصيلكِ، وأناقة صُممت لتليق بكِ.",
];
const STORY_EN = [
  "RNF Line was not born to be just another abaya and fashion label — it was born from a passion for elegance and a belief that every woman deserves to find what feels like her.",
  "Our journey began with a simple idea: to offer designs that blend refinement with comfort, heritage with a modern touch, and quiet detail with an unforgettable presence.",
  "At RNF Line we care about every detail — from choosing the fabrics to the design and the final touches — to give you pieces that feel made just for you.",
  "We believe elegance follows no single mould, and that every woman has her own taste, character and way of expressing herself.",
  "So we craft carefully selected abayas and designs that bring together beauty, simplicity and refinement — to become part of your look and your story.",
  "RNF Line is not merely what you wear… it is a feeling that accompanies you, a confidence in your details, an elegance designed to suit you.",
];

function FeaturedCard({ product, testid, lang }) {
  const img = product.images?.[0];
  return (
    <Link to={`/product/${product.id}`} data-testid={testid} className="group block">
      <div className="relative aspect-[3/4] overflow-hidden bg-[#EFE7D8] rounded-sm">
        {img ? (
          <img src={resolveImg(img)} alt={product.name_ar}
            loading="eager" fetchPriority="high" decoding="async"
            className="w-full h-full object-contain object-center transition-transform duration-500 md:group-hover:scale-[1.03]" />
        ) : (<div className="absolute inset-0 animate-pulse" />)}
      </div>
      <div className="pt-3 text-center">
        <h3 className="font-display text-xl md:text-2xl text-[#141110]">{lang === "ar" ? product.name_ar : (product.name_en || product.name_ar)}</h3>
        <p className="font-num text-accent text-base mt-1" dir="ltr">{formatKWD(product.price, lang)}</p>
        <span className="inline-block mt-3 border border-[#141110]/70 text-[#141110] text-xs px-5 py-2 group-hover:bg-[#141110] group-hover:text-white transition-colors">
          {lang === "ar" ? "تفصيل حسب المقاس" : "Made to measure"}
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const { lang } = useLang();
  const ar = lang === "ar";
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const heroSlides = products.filter((p) => p.images?.length).slice(0, 5);

  const bySku = (sku) => products.find((p) => p.sku === sku);
  const featRight = bySku("RNF-007") || products[0];
  const featLeft = bySku("RNF-006") || products[1];
  const featuredIds = [featRight?.id, featLeft?.id].filter(Boolean);
  const rest = products.filter((p) => !featuredIds.includes(p.id));

  return (
    <div className="fade-in">
      <HeroSlider slides={heroSlides} />

      <section className="max-w-6xl mx-auto px-6 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 md:gap-16">
          {VALUES.map((v, i) => {
            const I = v.icon; const [t, d] = ar ? v.ar : v.en;
            return (
              <div key={i} className="flex flex-col items-center text-center gap-4" data-testid={`value-${i}`}>
                <I className="w-7 h-7 text-accent" strokeWidth={1.1} />
                <span className="w-8 h-px bg-[#B89C72]/40" />
                <div>
                  <div className="font-display text-xl text-[#141110] mb-1.5">{t}</div>
                  <p className="text-text-muted text-sm leading-relaxed max-w-[240px] mx-auto">{d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-6 md:pb-10">
        <div className="text-center mb-8 md:mb-12">
          <span className="text-xs tracking-[0.24em] uppercase text-accent">{ar ? "وصل حديثاً" : "New Arrivals"}</span>
          <h2 className="font-display text-3xl md:text-5xl mt-2 text-[#141110]">{ar ? "أحدث تصاميم رنف" : "Latest RNF Designs"}</h2>
        </div>
        {products.length === 0 ? (
          <div className="grid grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto">
            {[0, 1].map((i) => <div key={i} className="aspect-[3/4] bg-surface animate-pulse rounded-sm" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 md:gap-10 max-w-4xl mx-auto" data-testid="home-featured">
            {featRight && <FeaturedCard product={featRight} testid="featured-right" lang={lang} />}
            {featLeft && <FeaturedCard product={featLeft} testid="featured-left" lang={lang} />}
          </div>
        )}
      </section>

      {rest.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 pb-14 md:pb-20">
          <div className="flex items-end justify-between mb-4 md:mb-6">
            <h3 className="font-display text-2xl md:text-3xl text-[#141110]">{ar ? "تصاميم أخرى" : "Other Designs"}</h3>
            <Link to="/shop" data-testid="view-all-btn" className="btn-secondary px-6 py-2.5 text-sm shrink-0">{ar ? "عرض الكل" : "View all"}</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 md:gap-6 items-start" data-testid="home-grid">
            {rest.map((p, i) => (
              <ProductCard key={p.id} product={p} priority={i < 4} contain />
            ))}
          </div>
        </section>
      )}

      {products[0] && (
        <section className="grid md:grid-cols-2 items-stretch border-t border-[#B89C72]/25">
          <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[540px] bg-surface overflow-hidden">
            <img src={resolveImg(products[0].images?.[0])} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          </div>
          <div className="flex flex-col justify-center px-8 md:px-16 py-14 md:py-0 bg-[#F1EADD]/40">
            <span className="text-xs tracking-[0.22em] uppercase text-accent mb-4">{ar ? "تجربة التفصيل" : "The Tailoring Experience"}</span>
            <h2 className="font-display text-3xl md:text-4xl leading-snug mb-5 text-[#141110]">{ar ? "تُفصّل خصيصاً على مقاساتكِ أنتِ" : "Tailored exclusively to your measurements"}</h2>
            <p className="text-text-muted leading-relaxed max-w-md mb-8">{ar ? "اختاري الموديل، وأدخلي مقاساتكِ، ودعينا نخيط لكِ عباية تليق بكِ وحدك — بعنايةٍ وتفاصيل مدروسة." : "Choose a design, enter your measurements, and let us tailor an abaya made for you alone — with care and considered detail."}</p>
            <Link to="/shop" data-testid="madeto-cta" className="btn-primary self-start px-9 py-3.5 text-sm tracking-wide">{ar ? "اطلبي حسب مقاسك" : "Shop the Collection"}</Link>
          </div>
        </section>
      )}

      <section className="border-t border-[#B89C72]/25 bg-[#F1EADD]/30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
          <div className="flex flex-col items-center gap-2">
            <span className="text-sm md:text-base font-medium text-text-muted">{ar ? "تابعينا على إنستغرام" : "Follow us on Instagram"}</span>
            <a href="https://instagram.com/rnf_line" target="_blank" rel="noreferrer" data-testid="instagram-link"
              className="inline-flex items-center gap-2 text-xl md:text-2xl font-bold text-[#141110] hover:text-accent transition-colors">
              <Instagram className="w-6 h-6" strokeWidth={1.6} /> <span dir="ltr">@rnf_line</span>
            </a>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {products.slice(0, 4).map((p) => (
              <a key={p.id} href="https://instagram.com/rnf_line" target="_blank" rel="noreferrer"
                className="block aspect-[4/5] overflow-hidden bg-surface hover-zoom" data-testid={`insta-img-${p.id}`}>
                <img src={resolveImg(p.images?.[0])} alt="" loading="lazy" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-text-muted">
            <a href={`https://wa.me/96566382446`} target="_blank" rel="noreferrer" data-testid="home-whatsapp" className="inline-flex items-center gap-2 hover:text-accent transition-colors">
              <MessageCircle className="w-4 h-4" /> <span dir="ltr">+965 6638 2446</span>
            </a>
            <a href="mailto:ranfkw@gmail.com" data-testid="home-email" className="inline-flex items-center gap-2 hover:text-accent transition-colors">
              <Mail className="w-4 h-4" /> <span dir="ltr">ranfkw@gmail.com</span>
            </a>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {ar ? "الشويخ، مجمع A5، بوابة 3، محل 321" : "Shuwaikh, A5 Complex, Gate 3, Shop 321"}</span>
          </div>
        </div>
      </section>

      {(settings ? settings.story_enabled !== false : true) && (
      <section className="bg-[#F1EADD] text-[#141110] border-t border-[#B89C72]/30" data-testid="our-story">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.28em] uppercase text-accent">RNF LINE</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 text-[#141110]">{ar ? "قصتنا" : "Our Story"}</h2>
            <span className="block w-12 h-px bg-accent/50 mx-auto mt-5" />
          </div>
          <div className={`space-y-5 text-[15px] md:text-base leading-[2] text-[#4A3F34] ${ar ? "text-right" : "text-left"}`}>
            {(() => {
              const custom = ar ? (settings?.story_ar || "") : (settings?.story_en || "");
              const paras = custom.trim() ? custom.split(/\n+/).map((s) => s.trim()).filter(Boolean) : (ar ? STORY_AR : STORY_EN);
              return paras.map((p, i) => <p key={i}>{p}</p>);
            })()}
          </div>
          <div className="text-center mt-12">
            <div className="font-display text-2xl md:text-3xl text-accent">{ar ? "رَنف لاين" : "RNF Line"}</div>
            <p className="text-sm text-text-muted mt-2">{ar ? "لمسة من الرقي… صُممت لتليق بكِ." : "A touch of refinement… designed to suit you."}</p>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}
